import { TWidget, TViewCommonOption } from '@libs/types';
import clsx from 'clsx';
import React, { Activity, CSSProperties, ElementType } from 'react';
import { useThemeHook } from '../hooks/themeHook';
import { TCoreTextData } from '../types/data';
import { TCoreTextOptions, ALIGNMENT_ENUM, TTextTransformation } from '../types/options';
import { buildTextCssVars } from './buildTextCssVars';
import { mergeWithTheme } from '../utils/styles';
import { Highlighter } from './Highlighter';
import styles from './text.module.css';

const ALIGNMENT_CLASS: Record<string, string> = {
    [ALIGNMENT_ENUM.TOP_LEFT]: styles.alignTopLeft,
    [ALIGNMENT_ENUM.TOP_CENTER]: styles.alignTopCenter,
    [ALIGNMENT_ENUM.TOP_RIGHT]: styles.alignTopRight,
    [ALIGNMENT_ENUM.CENTER_LEFT]: styles.alignCenterLeft,
    [ALIGNMENT_ENUM.CENTER]: styles.alignCenter,
    [ALIGNMENT_ENUM.CENTER_RIGHT]: styles.alignCenterRight,
    [ALIGNMENT_ENUM.BOTTOM_LEFT]: styles.alignBottomLeft,
    [ALIGNMENT_ENUM.BOTTOM_CENTER]: styles.alignBottomCenter,
    [ALIGNMENT_ENUM.BOTTOM_RIGHT]: styles.alignBottomRight,
};

export type TCoreTextStyleProps = Pick<
    TCoreTextOptions,
    | 'textAlign'
    | 'maxLines'
    | 'alignment'
    | 'textTransformation'
    | 'main'
    | 'font'
    | 'fontStyle'
    | 'padding'
    | 'margin'
    | 'textTransform'
    | 'disabled'
    | 'error'
>;

export type TCoreTextViewProps = {
    /**
     * Used solely for theme resolution via useThemeHook. No behavior or interaction logic is derived from it here.
     * Optional: when omitted, a minimal widget is synthesized internally from `text`. Provide it only when the
     * widget originates server-side (so theme styles via `widget.options.styleName` are honored).
     */
    widget?: TWidget<TCoreTextData, TCoreTextOptions>;
    uid?: string;
    text?: string;
    as?: ElementType;
    className?: string;
    /** Style overrides merged on top of the resolved theme palette via mergeWithTheme. */
    styleProps?: TCoreTextStyleProps;
    /** Called when the user clicks a named action span inside the Highlighter. */
    onClickAction?: (actionName: string) => void;
} & Pick<TViewCommonOption, 'isHide' | 'isDisabled' | 'isError' | 'styleName' | 'style' | 'parentStyleNames'>;

/**
 * Composable building block for use inside complex widgets.
 * Owns only the theme hook (useThemeHook) to keep the hook count low
 * when embedded inside larger widget trees.
 *
 * Accepts pre-resolved behavior props (isHide, isDisabled, onClickAction) from
 * the parent, making it suitable for scenarios where behavior state is already
 * managed one level up.
 *
 * `widget` is optional — when omitted, a minimal widget is synthesized from `text`,
 * which is sufficient for standalone usages that don't need server-driven theme styles.
 */
const CoreTextView = ({
    widget,
    uid,
    isError,
    isHide,
    isDisabled,
    text,
    as: Tag = 'p',
    className,
    styleProps,
    style,
    styleName,
    parentStyleNames,
    onClickAction,
}: TCoreTextViewProps) => {
    const resolvedWidget = widget ?? ({ uid: uid ?? '', data: { text }, options: {} } as TWidget<TCoreTextData, TCoreTextOptions>);
    // `styleName` and `parentStyleName` come from props only — the View does not read them off `widget.options`.
    // The Widget container (CoreTextWidget) is responsible for extracting `widget.options?.styleName`
    // and passing it as an explicit `styleName` prop.
    const enrichedWidget = { ...resolvedWidget, options: { ...resolvedWidget.options, style: styleName, parentStyleNames } };
    const { palette: themePalette, colors } = useThemeHook(enrichedWidget, 'text');
    const palette: TCoreTextOptions = mergeWithTheme(themePalette, styleProps);

    const content = resolvedWidget.data?.text;

    const { textTransformation: rawTransformation, maxLines, alignment } = palette;
    const textTransformation = (
        rawTransformation ? (Array.isArray(rawTransformation) ? rawTransformation : Object.values(rawTransformation)) : []
    ) as TTextTransformation[];

    return (
        <Activity mode={isHide ? 'hidden' : 'visible'}>
            <Tag
                data-test={resolvedWidget.uid}
                className={clsx(
                    styles.text,
                    isDisabled && styles.disabled,
                    maxLines && styles.maxLinesClamp,
                    alignment && ALIGNMENT_CLASS[alignment],
                    className,
                )}
                style={{ ...buildTextCssVars(palette, { isError, isDisabled }), ...style } as CSSProperties}
            >
                {content && <Highlighter content={content} textTransformation={textTransformation} colors={colors} onClickAction={onClickAction} />}
            </Tag>
        </Activity>
    );
};

export { CoreTextView };
